import React from 'react';
import { motion } from 'framer-motion';
import { Check, Star, MapPin, MessageSquare, Zap, Building2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut', delay },
});

export default function Plans({ onDemoClick }) {
  const { t } = useLanguage();
  const pl = t.plans;

  return (
    <section id="planes" className="py-14 md:py-20 bg-[#1F2833]" data-testid="plans-section">
      <div className="section-container">
        {/* Header */}
        <motion.div className="text-center mb-16" {...fadeUp()}>
          <div className="divider-gold mx-auto" />
          <h2 className="heading-lg mb-4">{pl.title}</h2>
          <p className="text-[#8A9099] text-base max-w-2xl mx-auto leading-relaxed">{pl.subtitle}</p>
        </motion.div>

        {/* Plans grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {pl.tiers.map((tier, i) => {
            const isFeatured = i === 1;
            return (
              <motion.div key={i}
                className={`relative rounded-2xl flex flex-col transition-all duration-300 overflow-hidden
                  ${isFeatured
                    ? 'bg-gradient-to-b from-[#0F6B49]/25 via-[#171E26] to-[#171E26] border-2 border-[#D5A63B]/60 shadow-[0_0_40px_rgba(213,166,59,0.2)] scale-[1.02]'
                    : 'bg-[#171E26] border border-[#0F6B49]/25 hover:border-[#D5A63B]/30 hover:shadow-[0_0_20px_rgba(213,166,59,0.08)]'
                  }`}
                {...fadeUp(0.1 + i * 0.1)} data-testid={`plan-card-${i}`}>

                {/* Featured top banner */}
                {isFeatured && (
                  <div className="bg-gradient-to-r from-[#D5A63B] to-[#f0c466] py-2.5 px-6 text-center">
                    <span className="flex items-center justify-center gap-1.5 text-[#1F2833] text-xs font-black uppercase tracking-wider font-exo">
                      <Star size={12} fill="currentColor" />
                      {tier.badge}
                      <Star size={12} fill="currentColor" />
                    </span>
                  </div>
                )}

                <div className="p-8 flex flex-col flex-1">
                  {/* Plan name */}
                  <h3 className={`font-exo font-bold italic text-xl mb-1 leading-tight ${isFeatured ? 'text-[#D5A63B]' : 'text-[#F3F1EB]'}`}
                    data-testid={`plan-name-${i}`}>
                    {tier.name}
                  </h3>

                  {/* Price */}
                  <div className="flex items-end gap-2 mt-4 mb-1">
                    <span className={`font-exo font-black text-5xl leading-none ${isFeatured ? 'text-[#D5A63B]' : 'text-[#F3F1EB]'}`}
                      data-testid={`plan-price-${i}`}>
                      {tier.price}
                    </span>
                    {tier.originalPrice && (
                      <span className="font-exo font-semibold text-xl leading-none text-[#8A9099] line-through mb-1"
                        data-testid={`plan-original-price-${i}`}>
                        {tier.originalPrice}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mb-5 flex-wrap">
                    <p className="text-[#8A9099] text-xs">{pl.perCampaign}</p>
                    {tier.discount && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider
                        text-[#1F2833] bg-[#D5A63B] px-2 py-0.5 rounded-md font-[Inter]"
                        data-testid={`plan-discount-${i}`}>
                        {tier.discount}
                      </span>
                    )}
                  </div>

                  {/* Key specs */}
                  <div className={`flex flex-wrap gap-2 mb-5 pb-5 border-b ${isFeatured ? 'border-[#D5A63B]/20' : 'border-white/5'}`}>
                    <div className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full
                      ${isFeatured ? 'bg-[#D5A63B]/10 text-[#D5A63B] border border-[#D5A63B]/25' : 'bg-[#0F6B49]/10 text-[#0F6B49] border border-[#0F6B49]/25'}`}>
                      <MapPin size={11} />
                      <span className="font-semibold">{tier.sedes}</span>
                    </div>
                    {tier.sms && (
                      <div className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full
                        ${isFeatured ? 'bg-[#D5A63B]/10 text-[#D5A63B] border border-[#D5A63B]/25' : 'bg-[#0F6B49]/10 text-[#0F6B49] border border-[#0F6B49]/25'}`}>
                        <MessageSquare size={11} />
                        <span className="font-semibold">{tier.sms}</span>
                      </div>
                    )}
                    {tier.addOn && (
                      <div className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#8A9099]/10 text-[#8A9099] border border-[#8A9099]/20">
                        <Building2 size={11} />
                        <span>{tier.addOn}</span>
                      </div>
                    )}
                  </div>

                  {/* CollectSticker integration badge */}
                  {tier.collectSticker && (
                    <div className="mb-4 bg-[#D5A63B]/10 border border-[#D5A63B]/30 rounded-xl px-4 py-3 flex items-start gap-2">
                      <Zap size={14} className="text-[#D5A63B] flex-shrink-0 mt-0.5" />
                      <p className="text-[#D5A63B] text-xs font-semibold leading-snug">{tier.collectSticker}</p>
                    </div>
                  )}

                  {/* Description */}
                  <p className="text-[#8A9099] text-sm leading-relaxed mb-6">{tier.desc}</p>

                  {/* Features */}
                  <ul className="space-y-2.5 flex-1 mb-8">
                    {tier.features.map((feat, j) => (
                      <li key={j} className="flex items-start gap-2.5">
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5
                          ${isFeatured ? 'bg-[#D5A63B]/20 border border-[#D5A63B]/40' : 'bg-[#0F6B49]/20 border border-[#0F6B49]/35'}`}>
                          <Check size={9} className={isFeatured ? 'text-[#D5A63B]' : 'text-[#0F6B49]'} />
                        </div>
                        <span className="text-[#F3F1EB]/75 text-sm leading-snug">{feat}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  {tier.paymentUrl ? (
                    <a href={tier.paymentUrl} target="_blank" rel="noopener noreferrer"
                      className={`w-full block text-center py-4 rounded-xl font-black font-exo italic text-base transition-all duration-300 no-underline
                        ${isFeatured
                          ? 'btn-primary shadow-[0_0_20px_rgba(213,166,59,0.3)] hover:shadow-[0_0_30px_rgba(213,166,59,0.5)]'
                          : 'btn-secondary hover:bg-[#0F6B49]/10'
                        }`}
                      data-testid={`plan-cta-${i}`}>
                      {tier.cta}
                    </a>
                  ) : (
                    <button onClick={onDemoClick}
                      className={`w-full py-4 rounded-xl font-black font-exo italic text-base transition-all duration-300
                        ${isFeatured
                          ? 'btn-primary shadow-[0_0_20px_rgba(213,166,59,0.3)] hover:shadow-[0_0_30px_rgba(213,166,59,0.5)]'
                          : 'btn-secondary hover:bg-[#0F6B49]/10'
                        }`}
                      data-testid={`plan-cta-${i}`}>
                      {tier.cta}
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Custom proposal */}
        <motion.div className="mt-10 bg-gradient-to-r from-[#0F6B49]/15 to-[#D5A63B]/10 border border-[#D5A63B]/20 rounded-2xl p-6 md:p-8
          flex flex-col md:flex-row items-start md:items-center justify-between gap-5"
          {...fadeUp(0.45)} data-testid="plans-custom-proposal">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Building2 size={18} className="text-[#D5A63B]" />
              <h3 className="font-exo font-bold italic text-[#F3F1EB] text-lg">{pl.customTitle}</h3>
            </div>
            <p className="text-[#8A9099] text-sm">{pl.customText}</p>
          </div>
          <button onClick={onDemoClick}
            className="btn-secondary flex-shrink-0 text-sm px-6 py-3"
            data-testid="plans-custom-cta">
            {pl.customCta}
          </button>
        </motion.div>
      </div>
    </section>
  );
}

